import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ToastContainer } from "react-toastify";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faRobot, faFire, faHeart, faMagnifyingGlass, faChartLine, faSeedling } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../Pages/Login/UserContext";
import AdminNotices from "../components/AdminNotices";
import { formatMubouScore, getScoreMagnitude } from "../utils/scoreDisplay";

/* ── アニメーション ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ── ヒーローエリア ── */
const HeroSection = styled.section`
  background: linear-gradient(-45deg, #0f0c29, #302b63, #24243e, #1a1a2e);
  background-size: 400% 400%;
  animation: ${gradientShift} 12s ease infinite;
  padding: 50px 16px 40px;
  text-align: center;
`;

const HeroLogo = styled.img`
  width: clamp(200px, 40vw, 360px);
  height: auto;
  margin-bottom: 12px;
  animation: ${fadeInUp} 0.7s ease both;
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 700px;
  height: auto;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 0 30px rgba(255, 100, 0, 0.2);
  animation: ${fadeInUp} 0.7s ease 0.15s both;
`;

const LogoSubtitle = styled.p`
  font-size: clamp(0.75rem, 2vw, 0.9rem);
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.15em;
  margin: -4px 0 18px;
  animation: ${fadeInUp} 0.7s ease 0.1s both;
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.3rem, 4.5vw, 2.4rem);
  font-weight: 900;
  background: linear-gradient(90deg, #ffd700, #ff8c00, #ffd700);
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
`;

const HeroSub = styled.p`
  font-size: clamp(0.85rem, 2.5vw, 1.05rem);
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 28px;
  line-height: 2;
  animation: ${fadeInUp} 0.7s ease 0.2s both;
`;

const HeroButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.7s ease 0.4s both;
`;

const SearchBtnRow = styled.div`
  margin: 16px auto 0;
  width: 100%;
  max-width: 360px;
  animation: ${fadeInUp} 0.7s ease 0.5s both;
`;

const SearchBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  padding: 13px 28px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-size: clamp(0.88rem, 2.4vw, 0.98rem);
  font-weight: 700;
  text-decoration: none;
  box-sizing: border-box;
  line-height: 1;
  white-space: nowrap;
  letter-spacing: 0.02em;
  transition: background 0.25s ease, border-color 0.25s ease,
              transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.7);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    text-decoration: none;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    font-size: 0.92rem;
  }
`;

const HeroHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 860px;
  margin: 28px auto 0;
  animation: ${fadeInUp} 0.7s ease 0.5s both;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HighlightCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 16px 18px;
  text-align: left;
  backdrop-filter: blur(8px);
`;

const HighlightLabel = styled.div`
  color: #ffd782;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
`;

const HighlightText = styled.p`
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.92rem;
  line-height: 1.7;
  margin: 0;
`;

const SpotlightWrap = styled.section`
  max-width: 920px;
  margin: -18px auto 0;
  padding: 0 16px 24px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-top: -8px;
    padding: 0 10px 18px;
  }
`;

const SpotlightGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SpotlightCard = styled.div`
  background: ${(props) =>
    props.$accent
      ? "linear-gradient(135deg, #fffdfa 0%, #fff8ec 100%)"
      : "linear-gradient(135deg, #f7f8ff 0%, #eef2ff 100%)"};
  border: 1px solid ${(props) => (props.$accent ? "#ead8ad" : "#d8dffb")};
  border-radius: 18px;
  padding: 18px 18px 16px;
  box-shadow: 0 12px 28px rgba(19, 17, 51, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 34px rgba(19, 17, 51, 0.12);
  }
`;

const SpotlightLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: ${(props) => (props.$accent ? "#8f6d28" : "#4d5dc7")};
  margin-bottom: 8px;
`;

const SpotlightTitle = styled.h3`
  font-size: clamp(1.05rem, 3vw, 1.3rem);
  font-weight: 900;
  color: #242424;
  margin: 0 0 8px;
  line-height: 1.45;
`;

const SpotlightText = styled.p`
  color: #514a3c;
  font-size: 0.95rem;
  line-height: 1.75;
  margin: 0 0 12px;
`;

const SpotlightMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SpotlightPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  color: #56411e;
  font-size: 0.78rem;
  font-weight: 700;
`;

const HeroBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 700;
  font-size: clamp(0.9rem, 2.5vw, 1.05rem);
  box-sizing: border-box;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    text-decoration: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.95rem;
  }
`;

const PrimaryBtn = styled(HeroBtn)`
  background: linear-gradient(135deg, #f12711, #f5af19);
  color: #fff;
  &:hover { color: #fff; }
`;

const SecondaryBtn = styled(HeroBtn)`
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.5);
  color: #fff;
  &:hover { color: #fff; border-color: #fff; }
`;

/* ── ランキングエリア ── */
const RankingArea = styled.section`
  max-width: 700px;
  margin: 0 auto;
  padding: 36px 16px 60px;

  @media (max-width: 768px) {
    padding: 24px 8px 40px;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.1rem, 3.5vw, 1.45rem);
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 32px;
  position: relative;

  &::after {
    content: "";
    display: block;
    width: 48px;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 999px;
    margin: 10px auto 0;
  }
`;

const RankingSection = styled.div`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.6s ease ${(props) => 0.1 * props.$index}s both;
`;

/* ── セクションヘッダー ── */
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  padding: 0 2px;
`;

const SectionIconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${(props) => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px ${(props) => props.$shadow};
`;

const SectionLabelText = styled.h3`
  flex: 1;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.01em;
`;

const SectionTag = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: ${(props) => props.$color};
  background: ${(props) => props.$bg};
  border: 1px solid ${(props) => props.$border};
  padding: 4px 11px;
  border-radius: 999px;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, ${(props) => props.$color}50, transparent);
  margin: 10px 0 14px;
`;

const RankCard = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  overflow: hidden;
  background: ${(props) => {
    if (props.$rank === 1) return "linear-gradient(135deg, #ffd700 0%, #ffec80 100%)";
    if (props.$rank === 2) return "linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)";
    if (props.$rank === 3) return "linear-gradient(135deg, #cd7f32 0%, #e8c07a 100%)";
    return "#fff";
  }};
  border-left: ${(props) =>
    props.$rank > 3 ? `3px solid ${props.$accentColor}40` : "none"};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 10px 10px;
  }
`;

const RankNum = styled.div`
  font-size: ${(props) => (props.$rank <= 3 ? "24px" : "16px")};
  font-weight: 900;
  min-width: 28px;
  flex-shrink: 0;
  text-align: center;
  color: ${(props) => (props.$rank <= 3 ? "#333" : "#999")};
`;

const RankInfo = styled.div`
  flex: 1;
  margin-left: 10px;
  min-width: 0;
  overflow: hidden;
`;

const RankItemTitle = styled.div`
  font-weight: bold;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RankItemScore = styled.div`
  font-weight: 900;
  font-size: 14px;
  color: ${(props) => props.$color || "#667eea"};
  background: ${(props) => props.$bg || "transparent"};
  padding: ${(props) => (props.$bg ? "3px 9px" : "0")};
  border-radius: ${(props) => (props.$bg ? "999px" : "0")};
  margin-left: 8px;
  text-align: right;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: keep-all;
  line-height: 1.12;
  max-width: 42%;
  flex-shrink: 1;
`;

const MoreLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  margin: 16px auto 0;
  padding: 10px 20px;
  border-radius: 50px;
  border: 2px solid ${(props) => props.$color || "#667eea"};
  background: transparent;
  color: ${(props) => props.$color || "#667eea"};
  font-weight: bold;
  font-size: 13px;
  box-sizing: border-box;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.2s ease;
  &:hover {
    background: ${(props) => props.$color || "#667eea"};
    color: #fff;
    text-decoration: none;
  }
`;

/* ── スコア表示 & ソートヘルパー ── */
const formatScoreDisplay = (goal) => formatMubouScore(goal);

/* ── セクション定義 ── */
const SECTION_STYLES = {
  reality: {
    gradient: "linear-gradient(135deg, #22c55e 0%, #84cc16 100%)",
    shadow: "rgba(34,197,94,0.35)",
    icon: faSeedling,
    label: "進み始めた夢",
    tag: "MOVE",
    tagColor: "#16a34a",
    tagBg: "rgba(34,197,94,0.08)",
    tagBorder: "rgba(34,197,94,0.2)",
    accentColor: "#22c55e",
    scoreColor: "#16a34a",
    scoreBg: "rgba(34,197,94,0.1)",
  },
  buzz: {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
    shadow: "rgba(245,158,11,0.32)",
    icon: faChartLine,
    label: "盛り上がっている夢",
    tag: "LIVE",
    tagColor: "#d97706",
    tagBg: "rgba(245,158,11,0.08)",
    tagBorder: "rgba(245,158,11,0.2)",
    accentColor: "#f59e0b",
    scoreColor: "#d97706",
    scoreBg: "rgba(245,158,11,0.1)",
  },
  new: {
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    shadow: "rgba(102,126,234,0.35)",
    icon: faBolt,
    label: "新着の夢",
    tag: "NEW",
    tagColor: "#667eea",
    tagBg: "rgba(102,126,234,0.08)",
    tagBorder: "rgba(102,126,234,0.2)",
    accentColor: "#667eea",
    scoreColor: "#667eea",
    scoreBg: "rgba(102,126,234,0.08)",
  },
  ai: {
    gradient: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)",
    shadow: "rgba(6,182,212,0.35)",
    icon: faRobot,
    label: "AI 無謀度",
    tag: "A.I.",
    tagColor: "#0891b2",
    tagBg: "rgba(6,182,212,0.08)",
    tagBorder: "rgba(6,182,212,0.2)",
    accentColor: "#06b6d4",
    scoreColor: "#0891b2",
    scoreBg: "rgba(6,182,212,0.1)",
  },
  noway: {
    gradient: "linear-gradient(135deg, #f43f5e 0%, #f97316 100%)",
    shadow: "rgba(244,63,94,0.35)",
    icon: faFire,
    label: "できるわけないよ！",
    tag: "HOT",
    tagColor: "#e11d48",
    tagBg: "rgba(244,63,94,0.08)",
    tagBorder: "rgba(244,63,94,0.2)",
    accentColor: "#f43f5e",
    scoreColor: "#e11d48",
    scoreBg: "rgba(244,63,94,0.1)",
  },
  support: {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    shadow: "rgba(16,185,129,0.35)",
    icon: faHeart,
    label: "応援",
    tag: "CHEER",
    tagColor: "#059669",
    tagBg: "rgba(16,185,129,0.08)",
    tagBorder: "rgba(16,185,129,0.2)",
    accentColor: "#10b981",
    scoreColor: "#059669",
    scoreBg: "rgba(16,185,129,0.1)",
  },
};

/* ── コンポーネント ── */
function Home() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [aiRanking, setAiRanking] = useState([]);
  const [supportRanking, setSupportRanking] = useState([]);
  const [movingRanking, setMovingRanking] = useState([]);
  const [buzzRanking, setBuzzRanking] = useState([]);
  const [allDreams, setAllDreams] = useState([]);
  const { isLoggedIn } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const results = await Promise.all([
          axios.get(`${API_BASE_URL}/api/goals/?ordering=-reality_score&limit=10`),
          axios.get(`${API_BASE_URL}/api/goals/?ordering=-support_count&limit=10`),
          axios.get(`${API_BASE_URL}/api/goals/?ordering=-engagement_score&limit=10`),
          axios.get(`${API_BASE_URL}/api/goals/?ordering=-ai_score&limit=100`),
        ]);
        setMovingRanking(results[0].data);
        setSupportRanking(results[1].data);
        setBuzzRanking(results[2].data);
        setAllDreams(results[3].data);
        // 単位ランク → スコア順で正しくソート
        const aiSorted = [...results[3].data]
          .sort((a, b) => getScoreMagnitude(b) - getScoreMagnitude(a))
          .slice(0, 10);
        setAiRanking(aiSorted);
      } catch (err) {
        console.error("fetchGoals failed:", err);
      }
    };
    fetchGoals();
  }, [API_BASE_URL]);


  const dailySpotlight = (() => {
    if (!allDreams.length) return null;
    const today = new Date();
    const todayCandidates = allDreams.filter((goal) => {
      if (!goal.created_at) return false;
      const created = new Date(goal.created_at);
      return created.toDateString() === today.toDateString();
    });
    const source = todayCandidates.length ? todayCandidates : allDreams.slice(0, 20);
    return [...source].sort((a, b) => getScoreMagnitude(b) - getScoreMagnitude(a))[0] || null;
  })();

  const realityCandidate = (() => {
    const candidates = allDreams.filter(
      (goal) =>
        (goal.progress_count || 0) > 0 ||
        (goal.latest_progress_percentage || 0) > 0
    );
    if (!candidates.length) return null;
    const scoreGoal = (goal) => {
      const progressCount = goal.progress_count || 0;
      const progressPercent = goal.latest_progress_percentage || 0;
      const commentCount = goal.comment_count || 0;
      const supportCount = goal.support_count || 0;
      return progressCount * 20 + progressPercent + commentCount * 4 + supportCount * 2;
    };
    return [...candidates].sort((a, b) => scoreGoal(b) - scoreGoal(a))[0] || null;
  })();

  return (
    <>
      {/* ── ヒーロー ── */}
      <HeroSection>
        <HeroLogo
          src="/img/logo-muboyume.png"
          alt="無謀な夢を追う、愚か者たちへ"
          width="1200"
          height="362"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <LogoSubtitle>— むぼゆめ / 無謀な目標共有サイト —</LogoSubtitle>
        <HeroImage src="/img/GoalHero.png" alt="Arms Crossed" />
        <HeroTitle>笑われる夢から、世界は変わる。</HeroTitle>
        <HeroSub>
          まだ誰にも言えていない夢を、一行から。
          <br />
          AIの提案と人の応援で、小さな一歩を残そう。
        </HeroSub>
        <HeroButtons>
          <PrimaryBtn
            to={isLoggedIn ? "/Register" : "/StartDream"}
          >
            夢を投稿する
          </PrimaryBtn>
          <SecondaryBtn to="/Ranking">
            動き始めた夢を見る
          </SecondaryBtn>
        </HeroButtons>
        <SearchBtnRow>
          <SearchBtn to="/RegisterSearchView">
            <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
            条件で夢を探す
          </SearchBtn>
        </SearchBtnRow>
        <HeroHighlights>
          <HighlightCard>
            <HighlightLabel>POST</HighlightLabel>
            <HighlightText>
              くだらない夢でも、大きすぎる夢でもOK。まずは置いてみるところから始まる。
            </HighlightText>
          </HighlightCard>
          <HighlightCard>
            <HighlightLabel>REACTION</HighlightLabel>
            <HighlightText>
              AIが小さな一歩を提案。人からの応援やコメントも受け取れます。
            </HighlightText>
          </HighlightCard>
          <HighlightCard>
            <HighlightLabel>STORY</HighlightLabel>
            <HighlightText>
              うまくいったことも、立ち止まったことも。途中の一歩を残せます。
            </HighlightText>
          </HighlightCard>
        </HeroHighlights>
      </HeroSection>

      <AdminNotices scope="global" title="運営からのお知らせ" />

      {(dailySpotlight || realityCandidate) && (
        <SpotlightWrap>
          <SpotlightGrid>
            {dailySpotlight && (
              <SpotlightCard
                $accent
                onClick={() => navigate(`/boards/${dailySpotlight.id}`)}
              >
                <SpotlightLabel $accent>今日のひとこと無茶</SpotlightLabel>
                <SpotlightTitle>{dailySpotlight.title}</SpotlightTitle>
                <SpotlightText>
                  {dailySpotlight.ai_comment ||
                    "話が大きいだけで止まらない。とりあえず見に行きたくなる夢。"}
                </SpotlightText>
                <SpotlightMeta>
                  <SpotlightPill>無謀度 {formatScoreDisplay(dailySpotlight)}</SpotlightPill>
                  <SpotlightPill>コメント {dailySpotlight.comment_count || 0}件</SpotlightPill>
                </SpotlightMeta>
              </SpotlightCard>
            )}
            {realityCandidate && (
              <SpotlightCard onClick={() => navigate(`/boards/${realityCandidate.id}`)}>
                <SpotlightLabel>一歩を報告した夢</SpotlightLabel>
                <SpotlightTitle>{realityCandidate.title}</SpotlightTitle>
                <SpotlightText>
                  {realityCandidate.latest_progress_note ||
                    "少しずつ前に進み始めていて、ただの無茶で終わらなそうな気配がある。"}
                </SpotlightText>
                <SpotlightMeta>
                  <SpotlightPill>進捗 {realityCandidate.progress_count || 0}回</SpotlightPill>
                  {realityCandidate.latest_progress_percentage != null && (
                    <SpotlightPill>
                      最新 {realityCandidate.latest_progress_percentage}%
                    </SpotlightPill>
                  )}
                </SpotlightMeta>
              </SpotlightCard>
            )}
          </SpotlightGrid>
        </SpotlightWrap>
      )}

      {/* ── ランキング ── */}
      <RankingArea>
        <SectionTitle>みんなの夢と、小さな一歩</SectionTitle>
        <p style={{ textAlign: "center", color: "#555", lineHeight: 1.8 }}>現在はテスト公開中です。まずは一つの夢に、ひとこと応援を。</p>

        {/* 進み始めた夢 */}
        <RankingSection $index={0}>
          <SectionHeader>
            <SectionIconBadge
              $gradient={SECTION_STYLES.reality.gradient}
              $shadow={SECTION_STYLES.reality.shadow}
            >
              <FontAwesomeIcon icon={SECTION_STYLES.reality.icon} color="#fff" size="sm" />
            </SectionIconBadge>
            <SectionLabelText>{SECTION_STYLES.reality.label}</SectionLabelText>
            <SectionTag
              $color={SECTION_STYLES.reality.tagColor}
              $bg={SECTION_STYLES.reality.tagBg}
              $border={SECTION_STYLES.reality.tagBorder}
            >
              {SECTION_STYLES.reality.tag}
            </SectionTag>
          </SectionHeader>
          <SectionDivider $color={SECTION_STYLES.reality.accentColor} />
          {movingRanking.filter((item) => (item.progress_count || 0) > 0).slice(0, 3).map((item, i) => (
            <RankCard
              key={item.id}
              $rank={i + 1}
              $accentColor={SECTION_STYLES.reality.accentColor}
              onClick={() => navigate(`/boards/${item.id}`)}
            >
              <RankNum $rank={i + 1}>{i + 1}</RankNum>
              <RankInfo>
                <RankItemTitle>{item.title}</RankItemTitle>
              </RankInfo>
              <RankItemScore
                $color={SECTION_STYLES.reality.scoreColor}
                $bg={SECTION_STYLES.reality.scoreBg}
              >
                {item.latest_progress_percentage != null
                  ? `${item.latest_progress_percentage}%`
                  : `${item.progress_count || 0}回`}
              </RankItemScore>
            </RankCard>
          ))}
          {!movingRanking.some((item) => (item.progress_count || 0) > 0) && <p>最初の一歩の報告を待っています。</p>}
          <MoreLink to="/Ranking" $color={SECTION_STYLES.reality.tagColor}>もっと見る</MoreLink>
        </RankingSection>

        {/* 応援 */}
        <RankingSection $index={1}>
          <SectionHeader>
            <SectionIconBadge
              $gradient={SECTION_STYLES.support.gradient}
              $shadow={SECTION_STYLES.support.shadow}
            >
              <FontAwesomeIcon icon={SECTION_STYLES.support.icon} color="#fff" size="sm" />
            </SectionIconBadge>
            <SectionLabelText>{SECTION_STYLES.support.label}</SectionLabelText>
            <SectionTag
              $color={SECTION_STYLES.support.tagColor}
              $bg={SECTION_STYLES.support.tagBg}
              $border={SECTION_STYLES.support.tagBorder}
            >
              {SECTION_STYLES.support.tag}
            </SectionTag>
          </SectionHeader>
          <SectionDivider $color={SECTION_STYLES.support.accentColor} />
          {supportRanking.filter((item) => (item.support_count || 0) > 0).slice(0, 3).map((item, i) => (
            <RankCard
              key={item.id}
              $rank={i + 1}
              $accentColor={SECTION_STYLES.support.accentColor}
              onClick={() => navigate(`/boards/${item.id}`)}
            >
              <RankNum $rank={i + 1}>{i + 1}</RankNum>
              <RankInfo>
                <RankItemTitle>{item.title}</RankItemTitle>
              </RankInfo>
              <RankItemScore
                $color={SECTION_STYLES.support.scoreColor}
                $bg={SECTION_STYLES.support.scoreBg}
              >
                {item.support_count || 0}
              </RankItemScore>
            </RankCard>
          ))}
          {!supportRanking.some((item) => (item.support_count || 0) > 0) && <p>気になる夢を見つけたら、最初の応援を。</p>}
          <MoreLink to="/Ranking" $color={SECTION_STYLES.support.tagColor}>もっと見る</MoreLink>
        </RankingSection>

        {/* 盛り上がり */}
        <RankingSection $index={2}>
          <SectionHeader>
            <SectionIconBadge
              $gradient={SECTION_STYLES.buzz.gradient}
              $shadow={SECTION_STYLES.buzz.shadow}
            >
              <FontAwesomeIcon icon={SECTION_STYLES.buzz.icon} color="#fff" size="sm" />
            </SectionIconBadge>
            <SectionLabelText>{SECTION_STYLES.buzz.label}</SectionLabelText>
            <SectionTag
              $color={SECTION_STYLES.buzz.tagColor}
              $bg={SECTION_STYLES.buzz.tagBg}
              $border={SECTION_STYLES.buzz.tagBorder}
            >
              {SECTION_STYLES.buzz.tag}
            </SectionTag>
          </SectionHeader>
          <SectionDivider $color={SECTION_STYLES.buzz.accentColor} />
          {buzzRanking.filter((item) => (item.comment_count || 0) > 0).slice(0, 3).map((item, i) => (
            <RankCard
              key={item.id}
              $rank={i + 1}
              $accentColor={SECTION_STYLES.buzz.accentColor}
              onClick={() => navigate(`/boards/${item.id}`)}
            >
              <RankNum $rank={i + 1}>{i + 1}</RankNum>
              <RankInfo>
                <RankItemTitle>{item.title}</RankItemTitle>
              </RankInfo>
              <RankItemScore
                $color={SECTION_STYLES.buzz.scoreColor}
                $bg={SECTION_STYLES.buzz.scoreBg}
              >
                {item.comment_count || 0}件
              </RankItemScore>
            </RankCard>
          ))}
          {!buzzRanking.some((item) => (item.comment_count || 0) > 0) && <p>会話はこれから。気になる夢に、最初のひとことを。</p>}
          <MoreLink to="/Ranking" $color={SECTION_STYLES.buzz.tagColor}>もっと見る</MoreLink>
        </RankingSection>

        {/* AI無謀度 */}
        <RankingSection $index={3}>
          <SectionHeader>
            <SectionIconBadge
              $gradient={SECTION_STYLES.ai.gradient}
              $shadow={SECTION_STYLES.ai.shadow}
            >
              <FontAwesomeIcon icon={SECTION_STYLES.ai.icon} color="#fff" size="sm" />
            </SectionIconBadge>
            <SectionLabelText>{SECTION_STYLES.ai.label}</SectionLabelText>
            <SectionTag
              $color={SECTION_STYLES.ai.tagColor}
              $bg={SECTION_STYLES.ai.tagBg}
              $border={SECTION_STYLES.ai.tagBorder}
            >
              {SECTION_STYLES.ai.tag}
            </SectionTag>
          </SectionHeader>
          <SectionDivider $color={SECTION_STYLES.ai.accentColor} />
          {aiRanking.slice(0, 10).map((item, i) => (
            <RankCard
              key={item.id}
              $rank={i + 1}
              $accentColor={SECTION_STYLES.ai.accentColor}
              onClick={() => navigate(`/boards/${item.id}`)}
            >
              <RankNum $rank={i + 1}>{i + 1}</RankNum>
              <RankInfo>
                <RankItemTitle>{item.title}</RankItemTitle>
              </RankInfo>
              <RankItemScore
                $color={SECTION_STYLES.ai.scoreColor}
                $bg={SECTION_STYLES.ai.scoreBg}
              >
                {formatScoreDisplay(item)}
              </RankItemScore>
            </RankCard>
          ))}
          <MoreLink to="/Ranking" $color={SECTION_STYLES.ai.tagColor}>もっと見る</MoreLink>
        </RankingSection>
      </RankingArea>

      <ToastContainer position="top-center" autoClose={3000} />
    </>
  );
}

export default Home;

